/**
 * main-search.js — v5
 * M&S tarzı arama overlay.
 *
 * v5 DÜZELTMESİ (v4'teki "ekran yarım" sorunu):
 *  v4'te overlay yüksekliği visualViewport.height'a eşitleniyordu. iOS input'a
 *  focus'landığında visual viewport'u layout viewport içinde aşağı kaydırıyor
 *  (offsetTop > 0); position:fixed eleman layout viewport'a çakılı kaldığı için
 *  üstü ekran dışına çıkıyor, altı klavye hizasında bitiyordu → ekran yarım.
 *
 *  YENİ YAKLAŞIM:
 *   - Overlay HER ZAMAN tam ekran: height 100dvh. Asla küçülmüyor → boşluk yok.
 *   - Klavye yüksekliği (--ms-kb) sadece .ms-body'ye padding-bottom olarak
 *     veriliyor → son ürün klavyenin üstüne kadar scroll edilebiliyor.
 *   - Overlay inset:0 ile SABİT tam ekran; yüksekliği asla JS küçültmez
 *     yeniden hizalanıyor.
 *   - Ölçümler rAF ile throttle ediliyor.
 *
 * v4 DEĞİŞİKLİKLERİ (korunuyor):
 *  - Mobilde body scroll kilidi (donmuş görünmenin ana sebebi).
 *  - _watchBodyClass sadece AÇIK/KAPALI değişiminde çalışıyor.
 *  - Arama sonuçlarında ürün kartı slider'ı tek görsele indirildi.
 *  - 'input' event + 350ms debounce + min 2 karakter.
 *  - Yeniden aramada liste silinmiyor, soluklaşıyor (flicker yok).
 *  - resultLimit off-by-one + resultsList null guard.
 *  - SKU/barkod alanları data-search-fields'tan okunuyor (global patch yok).
 */

/* ═══════════════════════════════════════════════════════════════════════
   v6 — RENK ARAMASI
   Sorun: "kırmızı elbise" yazınca bütün elbiseler geliyordu. Renk, tek bir
   varyantın değil ürünün TAMAMININ verisi; siyah bir elbisenin kırmızı
   varyantı varsa metin araması onu da eşleştiriyor.

   Çözüm: Sorgudaki renk kelimesini q'dan çıkar, Shopify'ın kendi renk
   FİLTRESİNE çevir:  "kırmızı elbise" → q=elbise + renk filtresi = kırmızı

   ÖNEMLİ: Filtrenin parametre adı ve değeri SABİT YAZILMIYOR. Arama
   sayfasının kendi filtre listesinden okunuyor. Böylece mağazadaki yazım
   ("kırmızı" mı "Kırmızı" mı), filtrenin türü (varyant seçeneği / etiket /
   metafield) ve renk isimleri değişse bile kod bozulmuyor.
   ═══════════════════════════════════════════════════════════════════════ */

/* Renk sayılan kelimeler. Sadece "bu kelime renk mi?" kontrolü için;
   yazımın mağazadakiyle birebir olması GEREKMEZ, sadeleştirilerek
   karşılaştırılır. Desen adlarını da ekleyebilirsin (leopar, kazayağı...). */
const MS_COLOR_WORDS = [
  'kirmizi', 'bordo', 'sarabi', 'kiremit', 'nar cicegi', 'mercan', 'somon',
  'pembe', 'pudra', 'fusya', 'mor', 'murdum', 'lila',
  'mavi', 'acik mavi', 'lacivert', 'indigo', 'saks', 'turkuaz', 'petrol',
  'yesil', 'haki', 'mint', 'fistik yesili', 'yag yesili',
  'sari', 'hardal', 'okra', 'turuncu', 'taba', 'toprak',
  'kahverengi', 'kahve', 'camel', 'vizon', 'bej', 'krem', 'ekru', 'tas',
  'beyaz', 'siyah', 'gri', 'antrasit', 'kircilli', 'gumus', 'altin'
];

/* Türkçe karakterleri sadeleştirir: "KIRMIZI" / "Kırmızı" / "kirmizi" → "kirmizi" */
const msNormalize = (value) => String(value || '')
  .toLocaleLowerCase('tr')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/[^a-z0-9]/g, '');

const MS_COLOR_SET = MS_COLOR_WORDS.reduce((acc, word) => {
  acc[msNormalize(word)] = true;
  return acc;
}, {});

/* Bulunan filtreler burada saklanır → aynı renk için tekrar keşif yapılmaz.
   Değer null ise "bu renk için mağazada filtre yok" demektir. */
const MS_FILTER_CACHE = {};

/**
 * Sorguda renk kelimesi var mı?
 * Tek kelimelik sorgulara (sadece "kırmızı") dokunmaz.
 * @returns {{token: string, rest: string}|null}
 */
const msDetectColor = (query) => {
  const tokens = String(query || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;

  let token = null;
  const rest = [];

  tokens.forEach((word) => {
    const key = msNormalize(word);
    if (!token && MS_COLOR_SET[key]) {
      token = key;
    } else {
      rest.push(word);
    }
  });

  if (!token || !rest.length) return null;
  return { token: token, rest: rest.join(' ') };
};

/**
 * Filtre listesinde bu renge karşılık gelen gerçek parametreyi bulur.
 * Önce checkbox/radio input'larına, sonra filtre linklerine bakar.
 * @param {ParentNode} root  Arama sayfası (veya section) DOM'u
 * @returns {{name: string, value: string}|null}
 */
const msFindColorFilter = (root, token) => {
  const inputs = root.querySelectorAll('input[name^="filter."]');
  for (let i = 0; i < inputs.length; i++) {
    if (msNormalize(inputs[i].value) === token) {
      return { name: inputs[i].getAttribute('name'), value: inputs[i].value };
    }
  }

  const links = root.querySelectorAll('a[href*="filter."]');
  for (let i = 0; i < links.length; i++) {
    let params;
    try {
      params = new URL(links[i].getAttribute('href'), window.location.origin).searchParams;
    } catch (e) {
      continue;
    }
    let hit = null;
    params.forEach((value, key) => {
      if (!hit && key.indexOf('filter.') === 0 && msNormalize(value) === token) {
        hit = { name: key, value: value };
      }
    });
    if (hit) return hit;
  }

  return null;
};

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // ── Sabitler ──────────────────────────────────────────────────────
    this.MOBILE_BP     = 769;
    this.MIN_CHARS     = 2;
    this.DEBOUNCE_MS   = 350;
    this.LIMIT_MOBILE  = 6;
    this.LIMIT_DESKTOP = 8;

    // ── Element referansları ──────────────────────────────────────────
    this._input        = this.querySelector('.ms-form__input, .main-search__input');
    this._clearBtn     = this.querySelector('.ms-form__clear');
    this._defaultState = this.querySelector('#ms-default-state');
    this._resultsBox   = this.querySelector('#ms-results-state, .ms-results, .main-search__results');

    // ── Durum ─────────────────────────────────────────────────────────
    this._isOpen   = document.body.classList.contains('show-search');
    this._locked   = false;
    this._scrollY  = 0;
    this._oldQuery = '';
    this._rafID    = 0;

    // ── Kapat / Geri butonları ────────────────────────────────────────
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        this._closeOverlay();
      });
    });

    if (this._input) {
      this._input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape') this._closeOverlay();
      });
      this._input.addEventListener('input', () => this._syncClearBtn());
      // Klavye açılıp kapanırken resize event'i her zaman güvenilir gelmiyor
      this._input.addEventListener('focus', () => this._scheduleViewportSync(300));
      this._input.addEventListener('blur',  () => this._scheduleViewportSync(300));
    }

    // ── Temizle butonu ────────────────────────────────────────────────
    if (this._clearBtn) {
      this._clearBtn.addEventListener('click', () => {
        if (this._input) {
          this._input.value = '';
          this._input.focus({ preventScroll: true });
        }
        this._oldQuery = '';
        this._syncClearBtn();
        this._resetResults();
      });
    }

    this._initViewport();
    this._watchBodyClass();

    if (this.dataset.quickSearch === 'true') {
      this._initQuickSearch();
    }

    this._initProductSliders();
  }

  // ── Yardımcılar ─────────────────────────────────────────────────────
  _isMobile() {
    return window.innerWidth < this.MOBILE_BP;
  }

  _closeOverlay() {
    document.body.classList.remove('show-search');
  }

  _syncClearBtn() {
    if (!this._clearBtn || !this._input) return;
    this._clearBtn.classList.toggle('ms-show', this._input.value.length > 0);
  }

  _showDefault() {
    if (this._defaultState) this._defaultState.classList.remove('ms-hidden');
    if (this._resultsBox)   this._resultsBox.classList.remove('ms-show');
    this.classList.remove('ms-loading', 'ms-has-results', 'ms-no-results');
  }

  _showResults() {
    if (this._defaultState) this._defaultState.classList.add('ms-hidden');
    if (this._resultsBox)   this._resultsBox.classList.add('ms-show');
  }

  _resetResults() {
    if (this._resultsBox) this._resultsBox.replaceChildren();
    this._showDefault();
  }

  // ── Viewport / klavye ölçümü ────────────────────────────────────────
  // Overlay yüksekliğine DOKUNMUYORUZ (inset:0 sabit tam ekran). Sadece:
  //   --ms-kb  : klavyenin kapladığı yükseklik → .ms-body padding-bottom
  _clearViewportVars() {
    this.style.removeProperty('--ms-kb');
  }

  _initViewport() {
    const vv = window.visualViewport;

    this._syncViewport = () => {
      this._rafID = 0;
      if (!this._isOpen || !this._isMobile() || !vv) {
        this._clearViewportVars();
        return;
      }

      // Klavye yüksekliği = layout viewport - (görünür yükseklik + kayma)
      let kb = window.innerHeight - (vv.height + vv.offsetTop);
      if (!isFinite(kb) || kb < 40) kb = 0;                      // gürültüyü ele
      kb = Math.min(kb, Math.round(window.innerHeight * 0.65));  // güvenlik sınırı

      this.style.setProperty('--ms-kb', Math.round(kb) + 'px');
    };

    this._scheduleViewportSync = (delay) => {
      if (delay) {
        setTimeout(() => this._scheduleViewportSync(0), delay);
        return;
      }
      if (this._rafID) return;
      this._rafID = requestAnimationFrame(this._syncViewport);
    };

    const onVV = () => this._scheduleViewportSync(0);

    if (vv) {
      vv.addEventListener('resize', onVV, { passive: true });
      vv.addEventListener('scroll', onVV, { passive: true });
    }
    window.addEventListener('resize', onVV, { passive: true });
    window.addEventListener('orientationchange', () => this._scheduleViewportSync(300), { passive: true });
  }

  // ── Body scroll kilidi (sadece mobil) ───────────────────────────────
  _lockScroll() {
    if (this._locked || !this._isMobile()) return;
    this._locked  = true;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${this._scrollY}px`;
    document.body.classList.add('ms-scroll-lock');
  }

  _unlockScroll() {
    if (!this._locked) return;
    this._locked = false;
    document.body.classList.remove('ms-scroll-lock');
    document.body.style.top = '';
    window.scrollTo(0, this._scrollY || 0);
  }

  // ── Ürün kartı slider (dot tıklama + swipe) ─────────────────────────
  // Arama sonuçlarında slider'ı tek görsele indiriyoruz; bu init sayfanın
  // geri kalanındaki kartlar için duruyor.
  _initProductSliders() {
    if (window.__nmxProductSliderInit) return;
    window.__nmxProductSliderInit = true;

    const updateSlider = (container, index) => {
      if (!container) return;
      const targetIndex = String(index);
      container.querySelectorAll('.custom-slide').forEach((slide) => {
        const active = slide.getAttribute('data-index') === targetIndex;
        slide.classList.toggle('is-active', active);
        slide.classList.toggle('is-hidden', !active);
      });
      container.querySelectorAll('.js-nav-dot').forEach((dot) => {
        dot.classList.toggle('active', dot.getAttribute('data-index') === targetIndex);
      });
    };

    document.addEventListener('click', (evt) => {
      const dot = evt.target.closest && evt.target.closest('.js-nav-dot');
      if (dot) {
        evt.preventDefault();
        evt.stopPropagation();
        updateSlider(dot.closest('.js-slider-parent'), dot.getAttribute('data-index'));
      }
    }, true);

    let startX = 0;
    let startY = 0;

    document.addEventListener('touchstart', (evt) => {
      const area = evt.target.closest && evt.target.closest('.js-swipe-area');
      if (area) {
        startX = evt.touches[0].clientX;
        startY = evt.touches[0].clientY;
      }
    }, { passive: true });

    document.addEventListener('touchend', (evt) => {
      const area = evt.target.closest && evt.target.closest('.js-swipe-area');
      if (!area) return;

      const deltaX = startX - evt.changedTouches[0].clientX;
      const deltaY = startY - evt.changedTouches[0].clientY;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 30) {
        const container = area.closest('.js-slider-parent');
        if (!container) return;
        const dots      = container.querySelectorAll('.js-nav-dot');
        const activeDot = container.querySelector('.js-nav-dot.active');
        if (activeDot && dots.length) {
          const current = parseInt(activeDot.getAttribute('data-index'), 10);
          const next = deltaX > 0
            ? (current + 1) % dots.length
            : (current - 1 + dots.length) % dots.length;
          updateSlider(container, next);
        }
      }
    }, { passive: true });
  }

  // ── Overlay açılış/kapanışını izle ──────────────────────────────────
  // KRİTİK: sadece durum DEĞİŞTİĞİNDE çalışır. Symmetry scroll sırasında
  // body'ye class ekleyip çıkardığı için eski sürümde her mutation'da
  // focus() + _showDefault() tetikleniyordu.
  _watchBodyClass() {
    const observer = new MutationObserver(() => {
      const isOpen = document.body.classList.contains('show-search');
      if (isOpen === this._isOpen) return;
      this._isOpen = isOpen;
      isOpen ? this._onOpen() : this._onClose();
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  _onOpen() {
    const pageHeader = document.getElementById('pageheader');

    if (this._isMobile()) {
      this._lockScroll();
      // Mobilde inline top/height YOK — CSS inset:0 yönetiyor
      this.style.top = '';
      this.style.height = '';
      this.style.maxHeight = '';
      this._scheduleViewportSync(0);
    } else {
      this._clearViewportVars();
      // Masaüstü: overlay top'unu header'ın gerçek bottom'una göre hizala
      const headerSection = document.querySelector('.section-header');
      if (headerSection) {
        const top = Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom));
        this.style.top       = top + 'px';
        this.style.maxHeight = `calc(100vh - ${top}px)`;
      }
    }

    // Header saydamsa opak görünüme geçir
    if (pageHeader && pageHeader.classList.contains('pageheader--transparent')) {
      pageHeader.classList.remove('pageheader--transparent');
      this._restoreTransparentHeader = true;
    }

    // Tek seferlik fokus
    setTimeout(() => {
      if (this._input) this._input.focus({ preventScroll: true });
      this._syncClearBtn();
      if (!this._input || !this._input.value.length) this._showDefault();
    }, 80);
  }

  _onClose() {
    const pageHeader = document.getElementById('pageheader');

    if (this._input) this._input.blur();
    this._clearViewportVars();
    this._unlockScroll();

    if (this._restoreTransparentHeader) {
      this._restoreTransparentHeader = false;
      if (pageHeader && window.scrollY <= 50) {
        pageHeader.classList.add('pageheader--transparent');
      }
    }
  }

  // ── Sonuç kartlarını hafifletme (PERF) ──────────────────────────────
  // Symmetry ürün kartı 4 adet .custom-slide render ediyor. 8 ürün = 32 görsel.
  // Arama sonuçlarında slider'a gerek yok: ilk slide dışındakileri atıyoruz.
  _slimProductBlock(block, index) {
    block.classList.add('main-search-result');

    block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
    block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));

    const slides = block.querySelectorAll('.custom-slide');
    if (slides.length > 1) {
      for (let i = slides.length - 1; i > 0; i--) slides[i].remove();
      slides[0].classList.add('is-active');
      slides[0].classList.remove('is-hidden');
    }

    block.querySelectorAll('.manual-dots-container').forEach((el) => el.remove());

    // Slider listener'larının boşa çalışmasını engelle
    block.classList.remove('js-slider-parent', 'js-swipe-area');
    block.querySelectorAll('.js-slider-parent').forEach((el) => el.classList.remove('js-slider-parent'));
    block.querySelectorAll('.js-swipe-area').forEach((el) => el.classList.remove('js-swipe-area'));

    // İlk 4 kart öncelikli, gerisi lazy
    block.querySelectorAll('img').forEach((img) => {
      img.decoding = 'async';
      if (index < 4) {
        img.loading = 'eager';
        img.setAttribute('fetchpriority', 'high');
      } else {
        img.loading = 'lazy';
        img.setAttribute('fetchpriority', 'low');
      }
    });

    return block;
  }

  // ── Quick search ────────────────────────────────────────────────────
  _initQuickSearch() {
    const input = this._input;
    if (!input || !this._resultsBox) return;

    const fields = this.dataset.searchFields ||
      'title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag';

    let timeoutID       = -1;
    let abortController = null;

    const fetchDOM = (url, signal) => fetch(url, { method: 'get', signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        return tpl.content;
      });

    const run = (query) => {
      const form     = input.closest('form');
      const baseLink = new URL(form.action, window.location.origin);
      new URLSearchParams(new FormData(form)).forEach((v, k) => baseLink.searchParams.set(k, v));

      const limit = this._isMobile() ? this.LIMIT_MOBILE : this.LIMIT_DESKTOP;
      const color = msDetectColor(query);

      abortController = new AbortController();
      const signal = abortController.signal;

      const done = (content, linkURL, sectionAPI) => {
        abortController = null;
        this._renderResults(content, limit, linkURL, sectionAPI);
      };

      const fail = (err) => {
        if (err && (err.name === 'AbortError' || String(err).indexOf('abort') !== -1)) return;
        console.warn('[ms-search]', err); // eslint-disable-line no-console
        this.classList.remove('ms-loading');
      };

      // ── Renkli sorgu ────────────────────────────────────────────────
      // Predictive search API filtre desteklemediği için arama sayfası
      // section olarak render ettiriliyor.
      if (color && MS_FILTER_CACHE[color.token] !== null) {
        const buildURL = (filter) => {
          const url = new URL(baseLink);
          url.searchParams.set('q', color.rest);
          if (filter) url.searchParams.set(filter.name, filter.value);
          return url;
        };
        const asSection = (url) => {
          const out = new URL(url);
          out.searchParams.set('section_id', 'main-search');
          return out;
        };

        const cached = MS_FILTER_CACHE[color.token];

        if (cached) {
          const link = buildURL(cached);
          fetchDOM(asSection(link), signal)
            .then((content) => done(content, link, true))
            .catch(fail);
          return;
        }

        // İlk kez: filtresiz sonucu çek, filtre listesinden gerçek
        // parametreyi keşfet, sonra filtreli sonucu çek.
        const probeLink = buildURL(null);
        fetchDOM(asSection(probeLink), signal)
          .then((content) => {
            const found = msFindColorFilter(content, color.token);
            MS_FILTER_CACHE[color.token] = found; // bulunamazsa null olarak işaretlenir

            if (!found) {
              done(content, probeLink, true);
              return null;
            }

            const link = buildURL(found);
            return fetchDOM(asSection(link), signal)
              .then((filtered) => done(filtered, link, true));
          })
          .catch(fail);
        return;
      }

      // ── Normal akış ─────────────────────────────────────────────────
      let ajaxURL;

      if (theme.Shopify.features.predictiveSearch) {
        ajaxURL = new URL(theme.routes.base + theme.routes.predictiveSearch, window.location.origin);
        ajaxURL.searchParams.set('q', query);
        ajaxURL.searchParams.set('section_id', 'predictive-search');
        ajaxURL.searchParams.set('resources[limit]', limit);
        // SKU / barkod dahil arama — global fetch patch'ine gerek yok
        ajaxURL.searchParams.set('resources[options][fields]', fields);
        ajaxURL.searchParams.set('resources[options][unavailable_products]', 'last');
      } else {
        ajaxURL = new URL(baseLink);
        ajaxURL.searchParams.set('section_id', 'main-search');
      }

      const sectionAPI = !theme.Shopify.features.predictiveSearch;

      fetchDOM(ajaxURL, signal)
        .then((content) => done(content, baseLink, sectionAPI))
        .catch(fail);
    };

    const onInput = () => {
      const query = input.value.trim();

      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      clearTimeout(timeoutID);

      if (!query.length) {
        this._oldQuery = '';
        this._resetResults();
        return;
      }

      if (query.length < this.MIN_CHARS) return;
      if (query === this._oldQuery) return;
      this._oldQuery = query;

      // İlk aramada spinner, sonrakilerde listeyi silmeden soluklaştır
      this._showResults();
      this.classList.add('ms-loading');
      if (!this.classList.contains('ms-has-results')) {
        const wrap = document.createElement('div');
        wrap.className = 'ms-spinner-wrap';
        wrap.innerHTML = '<div class="ms-spinner"></div>';
        this._resultsBox.replaceChildren(wrap);
      }

      timeoutID = setTimeout(() => run(query), this.DEBOUNCE_MS);
    };

    input.addEventListener('input', onInput);
    input.addEventListener('search', onInput);
  }

  // ── Sonuçları çiz ───────────────────────────────────────────────────
  // content    : fetch edilmiş DOM parçası
  // linkURL    : "Tüm sonuçları gör" bağlantısı
  // sectionAPI : section render mı (arama sayfası) yoksa predictive mi
  _renderResults(content, limit, linkURL, sectionAPI) {
    const resultsList = sectionAPI
      ? content.querySelector('.section-search-template .product-grid')
      : content.querySelector('.product-grid');

    this.classList.remove('ms-loading', 'ms-has-results', 'ms-no-results');

    if (!resultsList) {
      this._renderEmpty();
      return;
    }

    // ── Ürünler ──────────────────────────────────────────────────────
    const productsWrap = document.createElement('div');
    productsWrap.className = 'ms-results__products main-search__results__products collection-listing';
    const grid = document.createElement('div');
    grid.className = resultsList.className;
    productsWrap.appendChild(grid);

    const blocks = resultsList.querySelectorAll(
      '.product-block:not(.collection-block):not(.page-block)'
    );
    const frag = document.createDocumentFragment();
    let count = 0;

    blocks.forEach((block, i) => {
      if (i >= limit) return;
      frag.appendChild(this._slimProductBlock(block, i));
      count++;
    });
    grid.appendChild(frag);

    // ── Sayfalar ─────────────────────────────────────────────────────
    const pagesWrap = document.createElement('div');
    pagesWrap.className = 'ms-results__pages';

    resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
      const link  = block.querySelector('a');
      const title = block.querySelector('.page-block__title');
      if (!link || !title) return;
      const a = document.createElement('a');
      a.className = 'main-search-result main-search-result--page';
      a.href = link.href;
      const txt = document.createElement('div');
      txt.className = 'main-search-result__text';
      txt.innerText = title.innerText;
      a.appendChild(txt);
      pagesWrap.appendChild(a);
    });

    const hasProducts = count > 0;
    const hasPages    = !!pagesWrap.querySelector('.main-search-result');

    if (!hasProducts && !hasPages) {
      this._renderEmpty();
      return;
    }

    this.classList.add('ms-has-results');
    this.classList.toggle('main-search--results-on-multiple-lines', count > 4);

    const out = document.createDocumentFragment();
    if (hasProducts) out.appendChild(productsWrap);

    if (hasPages) {
      const h = document.createElement('h6');
      h.className = 'ms-results__pages-label';
      h.innerHTML = theme.strings.generalSearchPages;
      pagesWrap.insertAdjacentElement('afterbegin', h);
      out.appendChild(pagesWrap);
    }

    const allLink = document.createElement('a');
    allLink.className = 'ms-results__view-all btn btn--secondary';
    allLink.href = linkURL.toString();
    allLink.innerHTML = theme.strings.generalSearchViewAll;
    out.appendChild(allLink);

    // Tek seferde DOM'a yaz → tek reflow
    this._resultsBox.replaceChildren(out);
    this._showResults();
    this._scheduleViewportSync(0);
  }

  _renderEmpty() {
    this.classList.add('ms-no-results');
    const empty = document.createElement('div');
    empty.className = 'ms-results__empty main-search__empty-message';
    empty.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
    this._resultsBox.replaceChildren(empty);
    this._showResults();
  }
};

window.customElements.define('main-search', MainSearch);

/* ── Arama SONUÇ SAYFASI ───────────────────────────────────────────────
   /search?q=kırmızı elbise ile gelindiğinde (Enter, öneri linki, geçmiş,
   dış bağlantı) sayfadaki filtre listesinden gerçek renk filtresini bulur
   ve filtreli adrese geçer. Filtre bulunamazsa hiçbir şey yapmaz —
   sonuçlar eski haliyle görünür, boş sayfa riski yok.               */
(() => {
  if (window.__msColorRouteInit) return;
  window.__msColorRouteInit = true;

  const here = new URL(window.location.href);
  if (here.pathname.indexOf('/search') === -1) return;
  if (here.search.indexOf('filter.') !== -1) return; // döngü koruması

  const color = msDetectColor(here.searchParams.get('q'));
  if (!color) return;

  const apply = () => {
    const found = msFindColorFilter(document, color.token);
    if (!found) return;

    here.searchParams.set('q', color.rest);
    here.searchParams.set(found.name, found.value);
    window.location.replace(here.toString());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
})();